import React from "react";
import { Phone, Mail, Trash2, Wallet } from "lucide-react";
import { STATUS_OPTIONS } from "./adminFields";

const TAB_STYLES = {
  all: "border-primary/30 text-primary bg-white hover:bg-muted",
  new: "border-blue-200 text-blue-800 bg-blue-50 hover:bg-blue-100",
  contacted: "border-amber-200 text-amber-900 bg-amber-50 hover:bg-amber-100",
  confirmed: "border-emerald-200 text-emerald-800 bg-emerald-50 hover:bg-emerald-100",
  cancelled: "border-red-200 text-red-800 bg-red-50 hover:bg-red-100",
};

const PAY_BADGE = {
  unpaid: "bg-red-100 text-red-800",
  dp_paid: "bg-amber-100 text-amber-900",
  paid_full: "bg-emerald-100 text-emerald-800",
};
const PAY_LABEL = { unpaid: "Belum", dp_paid: "DP", paid_full: "Lunas" };

export default function BookingsTable({ bookings, onDelete, onStatusChange }) {
  const [filter, setFilter] = React.useState("all");

  const counts = React.useMemo(() => {
    const c = { all: bookings.length };
    STATUS_OPTIONS.forEach((s) => { c[s.v] = bookings.filter((b) => (b.status || "new") === s.v).length; });
    return c;
  }, [bookings]);

  const filtered = React.useMemo(
    () => (filter === "all" ? bookings : bookings.filter((b) => (b.status || "new") === filter)),
    [bookings, filter]
  );

  const Tab = ({ v, label }) => (
    <button
      onClick={() => setFilter(v)}
      data-testid={`booking-tab-${v}`}
      className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${TAB_STYLES[v]} ${filter === v ? "ring-2 ring-offset-1 ring-accent" : ""}`}
    >
      {label}
      <span className={`min-w-[22px] text-center rounded-full bg-white/70 border border-current/20 px-1.5 py-0.5 text-[10px]`}>
        {counts[v] || 0}
      </span>
    </button>
  );

  return (
    <div>
      {/* Kanban-style status filter tabs */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="booking-filter-tabs">
        <Tab v="all" label="Semua" />
        {STATUS_OPTIONS.map((s) => <Tab key={s.v} v={s.v} label={s.label} />)}
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-left">
              <tr>
                <th className="p-4 font-medium">Tanggal</th>
                <th className="p-4 font-medium">Paket</th>
                <th className="p-4 font-medium">Nama</th>
                <th className="p-4 font-medium">Kontak</th>
                <th className="p-4 font-medium">Pax</th>
                <th className="p-4 font-medium">Pembayaran</th>
                <th className="p-4 font-medium">Status</th>
                <th className="p-4 font-medium">Catatan</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="9" className="p-10 text-center text-muted-foreground">Tidak ada booking dengan status ini.</td></tr>
              )}
              {filtered.map((b) => (
                <tr key={b.id} className="border-t border-border" data-testid={`booking-row-${b.id}`}>
                  <td className="p-4 text-muted-foreground">{new Date(b.created_at).toLocaleDateString("id-ID")}</td>
                  <td className="p-4 font-medium">
                    {b.package_name}
                    {b.equipment_id && b.equipment_name && (
                      <div className="text-[10px] font-normal text-emerald-700 mt-0.5" data-testid={`equipment-tag-${b.id}`}>
                        + {b.equipment_name}
                        {b.equipment_price_idr ? ` · Rp ${(b.equipment_price_idr / 1000).toLocaleString("id-ID")}rb` : ""}
                      </div>
                    )}
                  </td>
                  <td className="p-4">{b.name}</td>
                  <td className="p-4">
                    <a href={`https://wa.me/${b.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:text-accent">
                      <Phone className="h-3.5 w-3.5" />{b.phone}
                    </a>
                    <a href={`mailto:${b.email}`} className="flex items-center gap-1 text-muted-foreground text-xs">
                      <Mail className="h-3 w-3" />{b.email}
                    </a>
                  </td>
                  <td className="p-4">{b.pax}</td>
                  <td className="p-4">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-widest rounded-full px-2.5 py-1 ${PAY_BADGE[b.payment_status || "unpaid"]}`}>
                      <Wallet className="h-3 w-3" />
                      {PAY_LABEL[b.payment_status || "unpaid"]}
                      {b.payment_status === "dp_paid" && b.dp_amount_idr > 0 && (
                        <span className="ml-1 opacity-80">Rp {(b.dp_amount_idr / 1000000).toFixed(1)}jt</span>
                      )}
                    </span>
                  </td>
                  <td className="p-4">
                    <select
                      value={b.status || "new"}
                      onChange={(e) => onStatusChange(b.id, e.target.value)}
                      data-testid={`status-select-${b.id}`}
                      className={`text-xs font-medium rounded-full px-3 py-1.5 border-0 cursor-pointer ${STATUS_OPTIONS.find(s => s.v === (b.status || "new"))?.color || "bg-gray-100"}`}
                    >
                      {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="p-4 text-muted-foreground max-w-xs truncate">{b.notes || b.departure_pref || "—"}</td>
                  <td className="p-4">
                    <button onClick={() => onDelete(b.id)} data-testid={`delete-booking-${b.id}`} className="text-destructive hover:bg-destructive/10 p-2 rounded">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
